import React, { useEffect, useState, useMemo } from "react";
import { PropTypes } from "prop-types";
import { backtoSevenDays } from "../../../../../../../../utils/displayTime";
import CustomTimeStamp from "../../../../../../../../generics/TimeStamp/customTimeStamp";
import css from "../../../../common/anomalyMagneticStyle.less";
import { nwAvailHmPayload } from "../../../../../../../../config/reqConfig/anomalyDetection";
import ApiService from "../../../../../../../../config/api-config";
import ADHeatMap from "../../../../genericComponents/ADHeatMap";
import Spinner from "../../../../../../../../common/Spinner";
import ErrorComponent from "../../../../../../../../common/ErrorComponent";
import NoDataAvailable from "../../../../../../../../common/NoDataAvailable";
import {
  generateHeatmapData,
  sitesWidgetVisibility,
} from "../../../../common/adUtils";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import useI18n from "../../../../../../../../i18n/useI18n";

const CircuitTrendAvailChart = (props) => {
  const { tViews } = useI18n("views");
  const [HbrBadge] = reactWrapper(["hbr-badge"]);
  const [timePeriod, setTimePeriod] = useState([]);
  const [loader, setLoader] = useState(null);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [noData, setNoData] = useState(null);

  useEffect(() => {
    // reset state
    setChartData(null);
    setNoData(null);
    setError(null);
    setLoader(null);
  }, [props.cuiProps.site]);

  const isRender = useMemo(() => {
    if (props.circuitList?.length > 0) {
      let circuitListNames = props.circuitList.map((item) => item.name);
      setNoData(false);
      if (
        props.circuitTrendList?.length === 0 ||
        circuitListNames.length !== props.circuitTrendList.length ||
        !circuitListNames.every((item) => props.circuitTrendList.includes(item))
      ) {
        props.isCircuitListSame(circuitListNames);
        return true;
      }
    } else {
      setNoData(true);
    }

    return false;
  }, [props.circuitList]);

  useEffect(() => {
    const runEffect = async () => {
      if (isRender) {
        try {
          setLoader(true);
          let timePeriod = backtoSevenDays();
          setTimePeriod(timePeriod);
          let circuitSelected = props.circuitList.map((item) => item.id);
          let payload = nwAvailHmPayload(timePeriod, circuitSelected);
          let res = await ApiService.getCircuitTrendAvailChart(payload);
          if (!res.errorObject) {
            let chartData = generateHeatmapData(
              res?.data?.data,
              timePeriod,
              props.circuitList,
            );
            setChartData(chartData);
            let selectedCircuits = props.circuitList.map((item) => item.name);
            props.isCircuitListSame(selectedCircuits);
          } else {
            setError(res.errorObject);
          }
        } catch (error) {
          setError(error);
        } finally {
          setLoader(false);
        }
      }
    };

    runEffect();
  }, [isRender]);

  const storedTab = localStorage.getItem("selectedADTab");
  sitesWidgetVisibility(storedTab);

  return (
    <div
      className={css["ad-circuit-trend-avail-chart"]}
      role="region"
      aria-label={tViews("usageBasedAD.circuitTrendByAvailability")}
    >
      <div aria-live="polite">
        {loader ? (
          <Spinner />
        ) : error ? (
          <ErrorComponent
            code={"500"}
            width={"110px"}
            className={"large-dashlet-error"}
          />
        ) : noData ? (
          <NoDataAvailable />
        ) : (
          chartData?.length > 0 && (
            <div>
              <h2>
                {tViews("usageBasedAD.circuitTrendByAvailability")}
                {tViews("postSpace")}
                <HbrBadge
                  slot="suffix"
                  pill
                  size="small"
                  aria-hidden="true"
                  data-testid="usage-based-ctavailability-site-badge"
                >
                  {`${tViews("usageBasedAD.Site")}: ${props.cuiProps.displaySiteName}`}
                </HbrBadge>
              </h2>
              {timePeriod && (
                <div className="customt-timestamp">
                  <CustomTimeStamp
                    prevTime={timePeriod[0]}
                    currentTime={timePeriod[1]}
                  />
                  <div className="ad-timePeriodTxt">{`${tViews("app360.afHeatmapTimePriodText")}`}</div>
                </div>
              )}
              <ADHeatMap
                data={chartData}
                circuitList={props.circuitList}
                id={"circuit-avail-trend-heatmap"}
                timePeriod={timePeriod}
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};

CircuitTrendAvailChart.propTypes = {
  circuitList: PropTypes.array.isRequired,
  refresh: PropTypes.bool,
  isCircuitListSame: PropTypes.func,
  circuitTrendList: PropTypes.array,
  cuiProps: PropTypes.object,
};

export default CircuitTrendAvailChart;
